import { type SearchFilterRequest } from '@pkgs/api/models';
import { type BigDecimalRangeDto } from '@generated/search-bff';

import { type OmitKeysStartingWith } from './utils';
import { type SearchListingsOptions } from './options';

export type RangeFilter = BigDecimalRangeDto;

export type SurfaceFilterKey = 'livingSurface' | 'plotSurface' | 'totalSurface';

// prefixes of filter keys that are simplified to a single filter (e.g. price -> salesPriceEur)
export type MappedFilterPrefixes = 'rentAny' | 'rentTotal' | 'salesPrice' | SurfaceFilterKey;

// keys that are either mapped to a different type or are set automatically
type DeprecatedFilterKeys = 'sortBy' | 'sortOrder' | 'marketingType';

export type SearchListingsFilters = Omit<OmitKeysStartingWith<SearchFilterRequest, MappedFilterPrefixes>, DeprecatedFilterKeys> & {
  price?: RangeFilter;
  livingSurface?: RangeFilter;
  plotSurface?: RangeFilter;
  totalSurface?: RangeFilter;
};

export type SearchState = {
  filters: SearchListingsFilters;
  options: SearchListingsOptions;
};

export type GetPropertiesByShopIdsFilter = Omit<SearchListingsFilters, 'masterDataShopIds' | 'shopIds'>;

export type GetPropertiesByLocationFilter = Omit<SearchListingsFilters, 'placeId'>;

export type GetPropertiesByAgentIdFilter = Omit<SearchListingsFilters, 'agentId'>;

export const PriceType = {
  SALES_PRICE: 'salesPrice',
  RENT_ANY: 'rentAny',
  RENT_TOTAL: 'rentTotal',
  RENT_NET: 'rentNet',
  RENT_NET_PER_SQUARE_METER: 'rentNetPerSquareMeter',
} as const;

export type PriceType = (typeof PriceType)[keyof typeof PriceType];

export const FeaturesFilter = {
  hasAirConditioning: 'hasAirConditioning',
  hasBalcony: 'hasBalcony',
  hasBasement: 'hasBasement',
  hasBuiltInKitchen: 'hasBuiltInKitchen',
  hasElevator: 'hasElevator',
  hasGarage: 'hasGarage',
  hasGarden: 'hasGarden',
  hasPatio: 'hasPatio',
  hasSeaOrLakeView: 'hasSeaOrLakeView',
  hasSecuritySystem: 'hasSecuritySystem',
  hasSwimmingPool: 'hasSwimmingPool',
  hasTennisCourt: 'hasTennisCourt',
  hasTerrace: 'hasTerrace',
  hasWaterfront: 'hasWaterfront',
  hasHelipad: 'hasHelipad',
  hasDoorman: 'hasDoorman',
  hasPriceUpdated: 'hasPriceUpdated',
} as const;

export type FeaturesFilter = (typeof FeaturesFilter)[keyof typeof FeaturesFilter];

export const MultimediaFilter = {
  hasFloorplan: 'hasFloorplan',
  hasVtourNormalized: 'hasVtourNormalized',
  hasVideoNormalized: 'hasVideoNormalized',
} as const;

export type MultimediaFilter = (typeof MultimediaFilter)[keyof typeof MultimediaFilter];
